import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { loadLists, ListItem, clearListsCache, ListsLanguage } from '../services/listsService';

interface ListsContextType {
  experts: ListItem[];
  persons: ListItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  clearCache: () => void;
}

const ListsContext = createContext<ListsContextType | undefined>(undefined);

interface ListsProviderProps {
  children: ReactNode;
}

function langFromI18n(lang: string): ListsLanguage {
  return lang === 'ru' || lang === 'en' ? lang : 'en';
}

export const ListsProvider: React.FC<ListsProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [experts, setExperts] = useState<ListItem[]>([]);
  const [persons, setPersons] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lang = langFromI18n(i18n.language);

  const loadListsData = async (forceReload: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`📋👥 [ListsContext] Загружаем списки экспертов и личностей (lang=${lang})...`);
      const { experts: loadedExperts, persons: loadedPersons } = await loadLists(lang, forceReload);
      
      setExperts(loadedExperts);
      setPersons(loadedPersons);
      setLoading(false);
      
      console.log(`📋👥 [ListsContext] Списки успешно загружены: ${loadedExperts.length} экспертов, ${loadedPersons.length} личностей (lang=${lang})`);
    } catch (err) {
      console.error('❌ [ListsContext] Ошибка загрузки списков:', err);
      
      try {
        console.log('📋👥 [ListsContext] Пытаемся получить fallback данные...');
        const { experts: fallbackExperts, persons: fallbackPersons } = await loadLists(lang, forceReload);
        
        setExperts(fallbackExperts);
        setPersons(fallbackPersons);
        console.log(`📋👥 [ListsContext] Fallback списки загружены: ${fallbackExperts.length} экспертов, ${fallbackPersons.length} личностей`);
      } catch (fallbackErr) {
        console.error('❌ [ListsContext] Критическая ошибка - не удалось получить даже fallback данные:', fallbackErr);
        setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
      }
      
      setLoading(false);
    }
  };

  const reload = async () => {
    console.log('🔄 [ListsContext] Принудительная перезагрузка списков');
    await loadListsData(true);
  };

  const clearCache = () => {
    console.log('🗑️ [ListsContext] Очистка кэша списков');
    clearListsCache();
  };

  // Загружаем списки при монтировании и при смене языка
  useEffect(() => {
    loadListsData();
  }, [lang]);

  const value: ListsContextType = {
    experts,
    persons,
    loading,
    error,
    reload,
    clearCache,
  };

  return (
    <ListsContext.Provider value={value}>
      {children}
    </ListsContext.Provider>
  );
};

export const useLists = (): ListsContextType => {
  const context = useContext(ListsContext);
  if (context === undefined) {
    throw new Error('useLists must be used within a ListsProvider');
  }
  return context;
};
